import React, { useState, useEffect } from 'react';
import CyberBadge from '../common/CyberBadge';
import {
  Building2,
  Plus,
  Eye,
  Home,
  XCircle,
  Users,
  TrendingUp,
  Trash2,
  Search,
  Save,
  RefreshCw
} from 'lucide-react';
import * as api from '../../api';

export default function PropertiesView({ rooms = [], onSelectDrawer, onOpenCreateModal, showTemporarySuccess }) {
  const [mainTab, setMainTab] = useState('properties'); // 'properties' | 'roommates' | 'market_intel'
  const [propertyFilter, setPropertyFilter] = useState('all'); // 'all' | 'pending' | 'verified' | 'sponsored' | 'rent' | 'buy' | 'pg'
  const [searchQuery, setSearchQuery] = useState('');

  // Roommate state
  const [roommates, setRoommates] = useState([]);
  const [loadingRoommates, setLoadingRoommates] = useState(false);

  // Market Intel state
  const [marketIntel, setMarketIntel] = useState(null);
  const [loadingIntel, setLoadingIntel] = useState(false);
  const [savingIntel, setSavingIntel] = useState(false);

  // Add Property Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProperty, setNewProperty] = useState({
    title: '',
    purpose: 'rent', // 'rent' | 'buy' | 'pg' | 'commercial'
    categoryType: 'flat',
    price: '',
    priceType: 'month',
    bhk: '2 BHK',
    sqft: '950',
    locality: 'Vijay Nagar',
    address: 'Near C21 Mall, Vijay Nagar, Indore',
    city: 'Indore',
    imageUrl: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=600&q=80',
    description: 'Spacious well-ventilated apartment with verified amenities.',
    isVerified: true,
    isSponsored: false,
    phone: '+91 98260 11223'
  });

  useEffect(() => {
    if (mainTab === 'roommates') {
      loadRoommates();
    } else if (mainTab === 'market_intel') {
      loadMarketIntel();
    }
  }, [mainTab]);

  const loadRoommates = async () => {
    setLoadingRoommates(true);
    try {
      const res = await api.getAdminRoommates();
      if (res.success && Array.isArray(res.data)) {
        setRoommates(res.data);
      }
    } catch (e) {
      console.warn('Failed to load roommates:', e.message);
    } finally {
      setLoadingRoommates(false);
    }
  };

  const loadMarketIntel = async () => {
    setLoadingIntel(true);
    try {
      const res = await api.getMarketIntel();
      if (res.success && res.data) {
        setMarketIntel(res.data);
      }
    } catch (e) {
      console.warn('Failed to load market intel:', e.message);
    } finally {
      setLoadingIntel(false);
    }
  };

  const handleSaveMarketIntel = async () => {
    if (!marketIntel) return;
    setSavingIntel(true);
    try {
      const res = await api.updateMarketIntel(marketIntel);
      if (res.success) {
        if (showTemporarySuccess) showTemporarySuccess('Market Intel Index updated and broadcasted!');
        else alert('Market Intel Index updated!');
      }
    } catch (e) {
      alert('Failed to update Market Intel: ' + e.message);
    } finally {
      setSavingIntel(false);
    }
  };

  const handleToggleVerified = async (roomId, currentVal) => {
    try {
      await api.toggleRoomFlag(roomId, 'isVerified', !currentVal);
      if (showTemporarySuccess) showTemporarySuccess(`Property verification ${!currentVal ? 'enabled' : 'disabled'}!`);
      // Update room in state
      const target = rooms.find((r) => (r._id || r.id) === roomId);
      if (target) {
        if (!target.flags) target.flags = {};
        target.flags.isVerified = !currentVal;
      }
    } catch (e) {
      alert('Error updating verification status: ' + e.message);
    }
  };

  const handleToggleSponsored = async (roomId, currentVal) => {
    try {
      await api.toggleRoomFlag(roomId, 'isSponsored', !currentVal);
      if (showTemporarySuccess) showTemporarySuccess(`Property sponsored status ${!currentVal ? 'boosted' : 'removed'}!`);
      const target = rooms.find((r) => (r._id || r.id) === roomId);
      if (target) {
        if (!target.flags) target.flags = {};
        target.flags.isSponsored = !currentVal;
      }
    } catch (e) {
      alert('Error updating sponsored status: ' + e.message);
    }
  };

  const handleDeleteRoommate = async (id) => {
    if (!window.confirm('Delete this roommate requirement?')) return;
    try {
      await api.deleteAdminRoommate(id);
      setRoommates((prev) => prev.filter((r) => r._id !== id && r.id !== id));
      if (showTemporarySuccess) showTemporarySuccess('Roommate requirement deleted');
    } catch (e) {
      alert(e.message);
    }
  };

  const handleAddPropertySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: newProperty.title,
        description: newProperty.description,
        price: parseFloat(newProperty.price) || 10000,
        priceType: newProperty.priceType,
        moduleType: 'building',
        categoryType: newProperty.purpose === 'pg' ? 'room_rent' : newProperty.categoryType,
        address: newProperty.address,
        city: newProperty.city,
        imageUrl: newProperty.imageUrl,
        contactInfo: {
          phone: newProperty.phone,
          whatsapp: newProperty.phone
        },
        moduleData: {
          bhk: newProperty.bhk,
          sqft: newProperty.sqft,
          purpose: newProperty.purpose,
          locality: newProperty.locality
        },
        flags: {
          isVerified: newProperty.isVerified,
          isSponsored: newProperty.isSponsored
        },
        status: 'approved'
      };

      const res = await api.createRoomDirect(payload);
      if (res.success) {
        if (showTemporarySuccess) showTemporarySuccess('New Property Listing Published!');
        setShowAddModal(false);
        window.location.reload();
      }
    } catch (err) {
      alert('Failed to publish property: ' + err.message);
    }
  };

  // Filter building rooms
  const buildingRooms = rooms.filter(
    (r) => r.moduleType === 'building' || r.categoryType === 'flat' || r.categoryType === 'room_rent' || r.categoryType === 'property'
  );

  const filteredProperties = buildingRooms.filter((item) => {
    const isVer = item.flags?.isVerified === true;
    const isSpon = item.flags?.isSponsored === true;
    const cat = (item.categoryType || '').toLowerCase();
    const purp = (item.moduleData?.purpose || '').toLowerCase();

    if (propertyFilter === 'pending' && item.status !== 'pending') return false;
    if (propertyFilter === 'verified' && !isVer) return false;
    if (propertyFilter === 'sponsored' && !isSpon) return false;
    if (propertyFilter === 'rent' && (item.priceType !== 'month' && purp !== 'rent')) return false;
    if (propertyFilter === 'buy' && (item.priceType === 'month' && purp !== 'buy')) return false;
    if (propertyFilter === 'pg' && !cat.includes('room') && !cat.includes('pg') && purp !== 'pg') return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = (item.title || '').toLowerCase().includes(q);
      const matchLoc = (item.city || item.location?.city || item.moduleData?.locality || '').toLowerCase().includes(q);
      if (!matchTitle && !matchLoc) return false;
    }

    return true;
  });

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Top Banner & Tab Switcher */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building2 size={28} style={{ color: 'var(--neon-green)' }} />
            <h1 className="page-title" style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0 }}>
              OurMaps Realty Console
            </h1>
          </div>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Full control over verified property listings, Saathi roommate requests, and the Indore Real Estate Index.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            className="btn btn-green"
            style={{ padding: '10px 18px', borderRadius: '10px', fontSize: '0.88rem' }}
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} /> Add Real Estate Listing
          </button>
        </div>
      </div>

      {/* Main Feature Tabs */}
      <div style={{ display: 'flex', gap: '10px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
        <button
          className={`btn ${mainTab === 'properties' ? 'btn-green' : 'btn-outline'}`}
          style={{ padding: '8px 18px', borderRadius: '8px', fontSize: '0.88rem' }}
          onClick={() => setMainTab('properties')}
        >
          <Home size={16} /> Properties (Rent & Sale) ({buildingRooms.length})
        </button>

        <button
          className={`btn ${mainTab === 'roommates' ? 'btn-green' : 'btn-outline'}`}
          style={{ padding: '8px 18px', borderRadius: '8px', fontSize: '0.88rem' }}
          onClick={() => setMainTab('roommates')}
        >
          <Users size={16} /> Saathi Roommates ({roommates.length || 'Active'})
        </button>

        <button
          className={`btn ${mainTab === 'market_intel' ? 'btn-green' : 'btn-outline'}`}
          style={{ padding: '8px 18px', borderRadius: '8px', fontSize: '0.88rem' }}
          onClick={() => setMainTab('market_intel')}
        >
          <TrendingUp size={16} /> Indore Market Intel & Rates
        </button>
      </div>

      {/* ───────────────────────────────────────────────────────────── */}
      {/* TAB 1: PROPERTIES (RENT & SALE) */}
      {/* ───────────────────────────────────────────────────────────── */}
      {mainTab === 'properties' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Sub Filters & Search */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { id: 'all', label: 'All Listings' },
                { id: 'verified', label: 'Verified ✓' },
                { id: 'sponsored', label: 'Sponsored ★' },
                { id: 'rent', label: 'For Rent' },
                { id: 'buy', label: 'For Sale' },
                { id: 'pg', label: 'PG & Sharing' },
                { id: 'pending', label: 'Pending Approval' }
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setPropertyFilter(f.id)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: propertyFilter === f.id ? 700 : 500,
                    background: propertyFilter === f.id ? 'var(--neon-green)' : 'rgba(255,255,255,0.06)',
                    color: propertyFilter === f.id ? '#0A0C14' : 'var(--text-secondary)',
                    border: '1px solid ' + (propertyFilter === f.id ? 'var(--neon-green)' : 'var(--border-color)'),
                    cursor: 'pointer'
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div style={{ position: 'relative', minWidth: '240px' }}>
              <Search size={15} style={{ position: 'absolute', left: '12px', top: '10px', color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Search properties or locality..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 36px',
                  borderRadius: '10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--border-color)',
                  color: '#FFFFFF',
                  fontSize: '0.85rem'
                }}
              />
            </div>
          </div>

          {filteredProperties.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(10,12,20,0.5)', borderRadius: '16px', border: '1px dashed var(--border-color)' }}>
              <Home size={36} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
              <div style={{ color: '#FFFFFF', fontSize: '1.1rem', fontWeight: 800 }}>No Matching Properties Found</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '4px' }}>
                Try adjusting your filter or click "Add Real Estate Listing" to upload.
              </div>
            </div>
          ) : (
            <div className="cyber-list-container">
              {filteredProperties.map((item, idx) => {
                const isVer = item.flags?.isVerified === true;
                const isSpon = item.flags?.isSponsored === true;
                const priceFormatted = Number(item.price) >= 10000000
                  ? `₹${(Number(item.price) / 10000000).toFixed(2)} Cr`
                  : Number(item.price) >= 100000
                  ? `₹${(Number(item.price) / 100000).toFixed(1)} Lakh`
                  : `₹${(Number(item.price) || 0).toLocaleString()}`;

                return (
                  <div key={item._id || item.id} className="cyber-list-row list-row-animate" style={{ animationDelay: `${idx * 0.05}s` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          background: '#151722',
                          border: '1px solid rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        {Array.isArray(item.images) && item.images[0] ? (
                          <img src={item.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Home size={22} style={{ color: 'var(--neon-green)' }} />
                        )}
                      </div>

                      <div>
                        <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          {item.title}
                          <CyberBadge status={item.status || 'approved'} />
                          {isVer && (
                            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '4px', background: 'rgba(217, 119, 6, 0.2)', color: '#F59E0B', border: '1px solid #F59E0B' }}>
                              VERIFIED ✓
                            </span>
                          )}
                          {isSpon && (
                            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '4px', background: 'rgba(234, 179, 8, 0.2)', color: '#EAB308', border: '1px solid #EAB308' }}>
                              SPONSORED ★
                            </span>
                          )}
                        </div>

                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>📍 {item.moduleData?.locality || item.city || item.location?.city || 'Indore'}</span>
                          <span>•</span>
                          <span>{item.moduleData?.bhk || 'Residential'}</span>
                          {item.moduleData?.sqft && <span>• {item.moduleData.sqft} sqft</span>}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--neon-green)', fontFamily: 'var(--font-mono)' }}>
                          {priceFormatted}
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            {item.priceType === 'month' ? '/mo' : ''}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {item.contactInfo?.phone || '+91 Owner'}
                        </div>
                      </div>

                      {/* Quick Action Toggle Buttons */}
                      <button
                        title={isVer ? 'Revoke Verified status' : 'Grant Verified badge'}
                        onClick={() => handleToggleVerified(item._id || item.id, isVer)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          background: isVer ? 'rgba(217, 119, 6, 0.15)' : 'rgba(255,255,255,0.06)',
                          color: isVer ? '#F59E0B' : 'var(--text-muted)',
                          border: '1px solid ' + (isVer ? '#F59E0B' : 'var(--border-color)'),
                          cursor: 'pointer'
                        }}
                      >
                        {isVer ? '✓ Verified' : '+ Verify'}
                      </button>

                      <button
                        title={isSpon ? 'Remove Sponsored tag' : 'Set as Sponsored Listing'}
                        onClick={() => handleToggleSponsored(item._id || item.id, isSpon)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          background: isSpon ? 'rgba(234, 179, 8, 0.15)' : 'rgba(255,255,255,0.06)',
                          color: isSpon ? '#EAB308' : 'var(--text-muted)',
                          border: '1px solid ' + (isSpon ? '#EAB308' : 'var(--border-color)'),
                          cursor: 'pointer'
                        }}
                      >
                        {isSpon ? '★ Sponsored' : '+ Sponsor'}
                      </button>

                      <button
                        className="btn btn-outline"
                        style={{ padding: '8px 12px', fontSize: '0.8rem' }}
                        onClick={() => onSelectDrawer(item, 'room')}
                      >
                        <Eye size={14} /> View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* TAB 2: SAATHI ROOMMATES & FLATMATES */}
      {/* ───────────────────────────────────────────────────────────── */}
      {mainTab === 'roommates' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#FFFFFF', margin: 0 }}>
                Live Roommate & Flatmate Requirements
              </h2>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                Requirements posted by seekers and flat-holders in Indore.
              </p>
            </div>

            <button
              className="btn btn-outline"
              style={{ padding: '6px 14px', fontSize: '0.8rem' }}
              onClick={loadRoommates}
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {loadingRoommates ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading requirements...</div>
          ) : roommates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', background: 'rgba(10,12,20,0.5)', borderRadius: '16px' }}>
              No roommate postings currently found.
            </div>
          ) : (
            <div className="cyber-list-container">
              {roommates.map((rm) => (
                <div key={rm._id || rm.id} className="cyber-list-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <img
                      src={rm.userAvatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80'}
                      alt=""
                      style={{ width: '46px', height: '46px', borderRadius: '50%', objectFit: 'cover' }}
                    />
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: 800, color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {rm.userName}
                        <span
                          style={{
                            fontSize: '0.7rem',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: rm.requirementType === 'have_room' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(56, 189, 248, 0.2)',
                            color: rm.requirementType === 'have_room' ? '#10B981' : '#38BDF8'
                          }}
                        >
                          {rm.requirementType === 'have_room' ? 'HAS ROOM' : 'SEEKING FLAT'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        📍 {Array.isArray(rm.preferredLocations) ? rm.preferredLocations.join(', ') : rm.preferredLocations || rm.city} • {rm.bhk || '2 BHK'} • {rm.occupation || 'Professional'}
                      </div>
                      {Array.isArray(rm.lifestyleHabits) && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                          {rm.lifestyleHabits.map((h, i) => (
                            <span key={i} style={{ fontSize: '0.68rem', padding: '1px 6px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', color: '#CBD5E1' }}>
                              {h}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--neon-green)' }}>
                        ₹{(rm.minBudget || 4000).toLocaleString()} - ₹{(rm.maxBudget || 12000).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{rm.userPhone}</div>
                    </div>

                    <button
                      className="btn btn-outline"
                      style={{ padding: '6px 10px', color: '#EF4444', borderColor: 'rgba(239,68,68,0.3)' }}
                      onClick={() => handleDeleteRoommate(rm._id || rm.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* TAB 3: INDORE REAL ESTATE MARKET INTEL */}
      {/* ───────────────────────────────────────────────────────────── */}
      {mainTab === 'market_intel' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#FFFFFF', margin: 0 }}>
                Indore Property Index & Micro-Locality Trends
              </h2>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                Rates and scores here directly power the "Market Intel" tab and PriceAI in the mobile app.
              </p>
            </div>

            <button
              className="btn btn-green"
              style={{ padding: '8px 18px', borderRadius: '8px' }}
              onClick={handleSaveMarketIntel}
              disabled={savingIntel}
            >
              <Save size={16} /> {savingIntel ? 'Saving...' : 'Save & Publish Live'}
            </button>
          </div>

          {loadingIntel || !marketIntel ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading market intel...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Overall Summary Config Card */}
              <div style={{ background: '#121520', borderRadius: '14px', padding: '20px', border: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                    Median Rate (₹/sqft)
                  </label>
                  <input
                    type="number"
                    value={marketIntel.medianSqft || 5784}
                    onChange={(e) => setMarketIntel({ ...marketIntel, medianSqft: Number(e.target.value) })}
                    className="form-control"
                    style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--neon-green)' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                    Verified Listings Count
                  </label>
                  <input
                    type="number"
                    value={marketIntel.verifiedCount || 57}
                    onChange={(e) => setMarketIntel({ ...marketIntel, verifiedCount: Number(e.target.value) })}
                    className="form-control"
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                    Active Captains in Indore
                  </label>
                  <input
                    type="number"
                    value={marketIntel.captainsActive || 6}
                    onChange={(e) => setMarketIntel({ ...marketIntel, captainsActive: Number(e.target.value) })}
                    className="form-control"
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                    Market Summary Text (Visible on Mobile)
                  </label>
                  <textarea
                    rows={2}
                    value={marketIntel.summaryText || ''}
                    onChange={(e) => setMarketIntel({ ...marketIntel, summaryText: e.target.value })}
                    className="form-control"
                  />
                </div>
              </div>

              {/* Localities Index Table */}
              <div style={{ background: '#121520', borderRadius: '14px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      <th style={{ padding: '12px 16px' }}>Locality</th>
                      <th style={{ padding: '12px 16px' }}>Avg ₹/sqft</th>
                      <th style={{ padding: '12px 16px' }}>1Y Apprec.</th>
                      <th style={{ padding: '12px 16px' }}>3Y Apprec.</th>
                      <th style={{ padding: '12px 16px' }}>Yield %</th>
                      <th style={{ padding: '12px 16px' }}>Score</th>
                      <th style={{ padding: '12px 16px' }}>Recommendation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(marketIntel.localities || []).map((loc, idx) => (
                      <tr key={loc.id || idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: '#FFFFFF' }}>
                          <span style={{ color: 'var(--neon-green)', marginRight: '6px' }}>{loc.code}</span> {loc.name}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <input
                            type="text"
                            value={loc.rateLabel || ''}
                            onChange={(e) => {
                              const updated = [...marketIntel.localities];
                              updated[idx].rateLabel = e.target.value;
                              setMarketIntel({ ...marketIntel, localities: updated });
                            }}
                            style={{ width: '110px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-color)', color: '#FFFFFF', fontSize: '0.8rem' }}
                          />
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <input
                            type="text"
                            value={loc.change1y || ''}
                            onChange={(e) => {
                              const updated = [...marketIntel.localities];
                              updated[idx].change1y = e.target.value;
                              setMarketIntel({ ...marketIntel, localities: updated });
                            }}
                            style={{ width: '80px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-color)', color: loc.change1y?.startsWith('+') ? '#10B981' : '#EF4444', fontSize: '0.8rem' }}
                          />
                        </td>
                        <td style={{ padding: '12px 16px', color: '#10B981' }}>{loc.change3y || '+20%'}</td>
                        <td style={{ padding: '12px 16px', color: '#38BDF8' }}>{loc.yield || '4%'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <input
                            type="number"
                            value={loc.score || 60}
                            onChange={(e) => {
                              const updated = [...marketIntel.localities];
                              updated[idx].score = Number(e.target.value);
                              setMarketIntel({ ...marketIntel, localities: updated });
                            }}
                            style={{ width: '60px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-color)', color: '#FFFFFF', fontSize: '0.8rem', fontWeight: 700 }}
                          />
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <select
                            value={loc.action || 'BUY'}
                            onChange={(e) => {
                              const updated = [...marketIntel.localities];
                              updated[idx].action = e.target.value;
                              setMarketIntel({ ...marketIntel, localities: updated });
                            }}
                            style={{ padding: '4px 8px', borderRadius: '6px', background: '#1A1D2D', border: '1px solid var(--border-color)', color: loc.action === 'BUY' ? '#F59E0B' : '#38BDF8', fontSize: '0.8rem', fontWeight: 700 }}
                          >
                            <option value="BUY">BUY</option>
                            <option value="HOLD">HOLD</option>
                            <option value="SELL">SELL</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* MODAL: ADD REAL ESTATE PROPERTY LISTING */}
      {/* ───────────────────────────────────────────────────────────── */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#121524', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '16px', border: '1px solid var(--neon-green)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#FFFFFF', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Home size={20} style={{ color: 'var(--neon-green)' }} /> Add Real Estate Property
              </h3>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <XCircle size={22} />
              </button>
            </div>

            <form onSubmit={handleAddPropertySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Property Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Compact Studio Scheme 78 - Budget Friendly"
                  value={newProperty.title}
                  onChange={(e) => setNewProperty({ ...newProperty, title: e.target.value })}
                  className="form-control"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Listing Purpose</label>
                  <select
                    className="form-control"
                    value={newProperty.purpose}
                    onChange={(e) => setNewProperty({ ...newProperty, purpose: e.target.value, priceType: e.target.value === 'rent' ? 'month' : 'one-time' })}
                  >
                    <option value="rent">For Rent (Monthly)</option>
                    <option value="buy">For Sale (Buy)</option>
                    <option value="pg">PG & Sharing</option>
                    <option value="commercial">Commercial Space</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>BHK / Room Type</label>
                  <select
                    className="form-control"
                    value={newProperty.bhk}
                    onChange={(e) => setNewProperty({ ...newProperty, bhk: e.target.value })}
                  >
                    <option value="1 BHK">1 BHK</option>
                    <option value="2 BHK">2 BHK</option>
                    <option value="3 BHK">3 BHK</option>
                    <option value="4 BHK">4 BHK</option>
                    <option value="Studio">Studio</option>
                    <option value="PG Room">PG Room</option>
                    <option value="Villa">Villa / Penthouse</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Price (₹) *</label>
                  <input
                    type="number"
                    required
                    placeholder="e.g. 10000 or 14000000"
                    value={newProperty.price}
                    onChange={(e) => setNewProperty({ ...newProperty, price: e.target.value })}
                    className="form-control"
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Area (Sqft)</label>
                  <input
                    type="number"
                    placeholder="e.g. 390 or 1200"
                    value={newProperty.sqft}
                    onChange={(e) => setNewProperty({ ...newProperty, sqft: e.target.value })}
                    className="form-control"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Locality (Indore)</label>
                  <select
                    className="form-control"
                    value={newProperty.locality}
                    onChange={(e) => setNewProperty({ ...newProperty, locality: e.target.value })}
                  >
                    <option value="Vijay Nagar">Vijay Nagar</option>
                    <option value="Super Corridor">Super Corridor</option>
                    <option value="Palasia">Palasia</option>
                    <option value="Nipania">Nipania</option>
                    <option value="Scheme 78">Scheme 78</option>
                    <option value="Bicholi Mardana">Bicholi Mardana</option>
                    <option value="MR-10">MR-10</option>
                    <option value="Bhawarkua">Bhawarkua</option>
                    <option value="Rau">Rau</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Owner / Vendor Phone</label>
                  <input
                    type="text"
                    placeholder="+91 98260 11223"
                    value={newProperty.phone}
                    onChange={(e) => setNewProperty({ ...newProperty, phone: e.target.value })}
                    className="form-control"
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Detailed Address</label>
                <input
                  type="text"
                  placeholder="Street / Society / Landmark"
                  value={newProperty.address}
                  onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })}
                  className="form-control"
                />
              </div>

              <div>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Photo Image URL</label>
                <input
                  type="text"
                  placeholder="https://images.unsplash.com/..."
                  value={newProperty.imageUrl}
                  onChange={(e) => setNewProperty({ ...newProperty, imageUrl: e.target.value })}
                  className="form-control"
                />
              </div>

              <div style={{ display: 'flex', gap: '20px', marginTop: '6px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#FFFFFF', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newProperty.isVerified}
                    onChange={(e) => setNewProperty({ ...newProperty, isVerified: e.target.checked })}
                    style={{ width: '16px', height: '16px', accentColor: 'var(--neon-green)' }}
                  />
                  Mark as Verified ✓
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#FFFFFF', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newProperty.isSponsored}
                    onChange={(e) => setNewProperty({ ...newProperty, isSponsored: e.target.checked })}
                    style={{ width: '16px', height: '16px', accentColor: '#EAB308' }}
                  />
                  Boost as Sponsored Listing ★
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '14px' }}>
                <button type="submit" className="btn btn-green" style={{ flex: 1, padding: '10px' }}>
                  Publish Property Listing
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  style={{ flex: 1, padding: '10px' }}
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
